import axiosInstance from "../axiosInstance";
import { appendWarehouseIds } from "../analyticsWarehouses";

export const metricsService = {
  /**
   * Client-scoped Sales Analysis calendar + coverage.
   * Calendar mode (fiscal | monthly) is resolved server-side from client-config.
   * Requires clientId (JWT client wins on the server when present).
   */
  fetchFiscalPeriods: async ({ clientId, fiscalYear } = {}) => {
    const params = new URLSearchParams();
    params.set("list", "fiscal_periods");
    if (clientId) params.set("client_id", clientId);
    if (fiscalYear) params.set("fiscal_year", fiscalYear);
    const response = await axiosInstance.get(
      `/metrics/sales-performance?${params.toString()}`,
    );
    return response.data;
  },

  /**
   * Fetch sales-performance metrics for one client + analysis period.
   * periodId is a fiscal id (FY26_P07) or monthly id (CY2026_M01) depending
   * on the client's sales_analysis_calendar_mode.
   * Server resolves client from JWT when present; client_id required for admins.
   *
   * `sourceChannels` uses the same Order Source serialization as Order
   * Management (`source_channel[$in][]=...`). Empty / omitted = all sources.
   * `warehouseIds` scopes sales and current inventory to the selected
   * warehouse(s); [] / omitted = all warehouses.
   */
  fetchSalesPerformance: async ({
    clientId,
    periodId,
    fiscalYear,
    includeDaily = true,
    includeHistory = true,
    sourceChannels = [],
    warehouseIds = [],
  }) => {
    const params = new URLSearchParams();
    params.set("period_id", periodId);
    if (clientId) params.set("client_id", clientId);
    // [] = All warehouses; one id keeps `warehouse_id=`, several use [$in][].
    appendWarehouseIds(params, warehouseIds);
    if (fiscalYear) params.set("fiscal_year", fiscalYear);
    if (includeDaily) params.set("include_daily", "true");
    if (includeHistory) params.set("include_history", "true");
    (sourceChannels || [])
      .filter((value) => value != null && value !== "")
      .forEach((value) => {
        params.append("source_channel[$in][]", value);
      });
    const response = await axiosInstance.get(
      `/metrics/sales-performance?${params.toString()}`,
    );
    return response.data;
  },

  /**
   * Lazy one-day SKU drilldown. Not fetched with the initial Sales Analysis
   * payload — call only after the user clicks a Daily Detail day.
   */
  fetchDailySkuBreakdown: async ({
    clientId,
    salesDay,
    sourceChannels = [],
    warehouseIds = [],
  } = {}) => {
    const params = new URLSearchParams();
    params.set("view", "daily_sku");
    params.set("sales_day", salesDay);
    if (clientId) params.set("client_id", clientId);
    // [] = All warehouses; one id keeps `warehouse_id=`, several use [$in][].
    appendWarehouseIds(params, warehouseIds);
    (sourceChannels || [])
      .filter((value) => value != null && value !== "")
      .forEach((value) => {
        params.append("source_channel[$in][]", value);
      });
    const response = await axiosInstance.get(
      `/metrics/sales-performance?${params.toString()}`,
    );
    return response.data;
  },
};
